import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Package, Mail, Truck, Save, User, Building, Upload } from 'lucide-react'
import { deliveryApi } from '../services/api'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import { showError } from '../utils/showError'
import { mapApiFieldErrors } from '../utils/errorUtils'
import PageHeader from '../components/common/PageHeader'
import LoadingSpinner from '../components/common/LoadingSpinner'
import Button from '../components/common/Button'

const CreateDelivery = () => {
  const navigate = useNavigate()
  const { selectedBuildingId, selectedBuildingName } = useAuth()
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [buildingUnits, setBuildingUnits] = useState([])
  const [searchingUsers, setSearchingUsers] = useState(false)
  const [userSearchTerm, setUserSearchTerm] = useState('')
  const [errors, setErrors] = useState({})
  
  const [formData, setFormData] = useState({
    // Required fields
    recipientUserId: '',
    buildingId: '',
    deliveryType: 1, // Package by default
    
    // Optional recipient details
    recipientName: '',
    recipientAddress: '',
    recipientUnitNumber: '',
    recipientFloorNumber: '',
    recipientPhone: '',
    recipientEmail: '',
    
    // Optional sender details
    senderAddress: '',
    senderName: '',
    serialNumber: '',
    
    // Building & Unit
    buildingUnitId: '',
    
    // Status
    isDelivered: false,
    deliveryDate: '',
    isCollected: false,
    collectedDate: '',
    
    // AI settings
    isAIGenerated: false,
    aiPrompt: '',

    // Attachments
    attachmentIds: []
  })

  // Sync building from global selector
  useEffect(() => {
    if (selectedBuildingId) {
      setFormData(prev => ({ ...prev, buildingId: selectedBuildingId }))
    }
  }, [selectedBuildingId])

  useEffect(() => {
    if (formData.buildingId) {
      loadBuildingUnits(formData.buildingId)
    }
  }, [formData.buildingId])

  const loadBuildingUnits = async (buildingId) => {
    try {
      const response = await api.get(`/Building/${buildingId}/units`)
      setBuildingUnits(response.data.items || [])
    } catch (error) {
      console.error('Error loading building units:', error)
      setBuildingUnits([])
    }
  }

  const searchUsers = async (searchTerm) => {
    try {
      setSearchingUsers(true)
      const response = await api.get('/Users', { 
        params: { 
          searchString: searchTerm,
          pageSize: 20
        }
      })
      setUsers(response.data.items || [])
    } catch (error) {
      console.error('Error searching users:', error)
      setUsers([])
    } finally {
      setSearchingUsers(false)
    }
  }

  // Driven by the operator typing rather than by the search term changing: picking a
  // result writes the chosen name into the box, and that must not reopen the dropdown.
  const handleUserSearchChange = (value) => {
    setUserSearchTerm(value)

    if (value.length >= 2) {
      searchUsers(value)
    } else {
      setUsers([])
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }

    // Auto-populate recipient details when user is selected
    if (field === 'recipientUserId' && value) {
      const selectedUser = users.find(u => u.userId === value)
      if (selectedUser) {
        setFormData(prev => ({
          ...prev,
          recipientName: `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim(),
          recipientEmail: selectedUser.email || '',
          recipientPhone: selectedUser.phoneNumber || ''
        }))
      }
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Required fields
    if (!formData.recipientUserId) {
      newErrors.recipientUserId = 'Recipient user is required'
    }
    if (!selectedBuildingId) {
      newErrors.buildingId = 'Please select a building from the top bar'
    }
    if (!formData.deliveryType) {
      newErrors.deliveryType = 'Delivery type is required'
    }

    // Conditional validations
    if (formData.isDelivered && !formData.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required when marked as delivered'
    }
    if (formData.isCollected && !formData.collectedDate) {
      newErrors.collectedDate = 'Collection date is required when marked as collected'
    }
    if (formData.isCollected && !formData.isDelivered) {
      newErrors.isDelivered = 'Cannot be collected without being delivered first'
    }

    // Email validation
    if (formData.recipientEmail && !/\S+@\S+\.\S+/.test(formData.recipientEmail)) {
      newErrors.recipientEmail = 'Invalid email format'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)
      
      // Build payload matching SaveDeliveryInput (Application DTO)
      const submitData = {
        recipientUserId: formData.recipientUserId,
        recipientName: formData.recipientName || undefined,
        recipientAddress: formData.recipientAddress || undefined,
        recipientUnitNumber: formData.recipientUnitNumber || undefined,
        recipientFloorNumber: formData.recipientFloorNumber || undefined,
        recipientPhone: formData.recipientPhone || undefined,
        recipientEmail: formData.recipientEmail || undefined,
        senderAddress: formData.senderAddress || undefined,
        senderName: formData.senderName || undefined,
        serialNumber: formData.serialNumber || undefined,
        buildingId: formData.buildingId,
        buildingUnitId: formData.buildingUnitId ? parseInt(formData.buildingUnitId) : undefined,
        deliveryType: formData.deliveryType,
        isAIGenerated: formData.isAIGenerated,
      }

      // Remove undefined fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === undefined) {
          delete submitData[key]
        }
      })

      console.log('Submitting delivery data:', submitData)

      const response = await deliveryApi.create(submitData)
      console.log('Delivery created successfully:', response.data)

      const deliveryId = response.data.deliveryId

      // Create always produces a pending delivery — SaveDeliveryInput carries no status.
      // Apply what the operator ticked through the status endpoint so a parcel already in
      // hand does not have to be created and then edited.
      if (formData.isDelivered || formData.isCollected) {
        try {
          await deliveryApi.updateStatus(deliveryId, {
            isDelivered: formData.isDelivered,
            isCollected: formData.isCollected,
            deliveryDate: formData.deliveryDate || undefined,
            collectedDate: formData.collectedDate || undefined,
          })
        } catch (statusError) {
          // The delivery exists, so still open it — but say that it stayed pending.
          console.error('Error applying delivery status:', statusError)
          showError(statusError)
        }
      }

      navigate(`/deliveries/${deliveryId}`)
    } catch (error) {
      console.error('Error creating delivery:', error)
      showError(error)

      // Handle API validation errors. The form's own keys are the known set, so
      // the mapping tracks the form rather than a hand-maintained lookup.
      if (error.response?.data?.errors) {
        setErrors(mapApiFieldErrors(error.response.data.errors, Object.keys(formData)))
      }
    } finally {
      setLoading(false)
    }
  }

  const getDeliveryTypeIcon = (type) => {
    switch (type) {
      case 1: return <Package className="w-5 h-5" />
      case 2: return <Mail className="w-5 h-5" />
      default: return <Truck className="w-5 h-5" />
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <div className="flex items-center">
            <button
              onClick={() => navigate('/deliveries')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            Create New Delivery
          </div>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Delivery Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Delivery Type *
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryType"
                      value={1}
                      checked={formData.deliveryType === 1}
                      onChange={(e) => handleInputChange('deliveryType', parseInt(e.target.value))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <Package className="ml-2 mr-1 w-4 h-4" />
                    <span className="text-sm text-gray-700">Package</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="deliveryType"
                      value={2}
                      checked={formData.deliveryType === 2}
                      onChange={(e) => handleInputChange('deliveryType', parseInt(e.target.value))}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <Mail className="ml-2 mr-1 w-4 h-4" />
                    <span className="text-sm text-gray-700">Mail</span>
                  </label>
                </div>
                {errors.deliveryType && (
                  <p className="mt-1 text-sm text-red-600">{errors.deliveryType}</p>
                )}
              </div>

              {/* Building */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building
                </label>
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
                  {selectedBuildingName || 'No building selected'}
                </div>
                {!selectedBuildingId && (
                  <p className="mt-1 text-sm text-red-600">Please select a building from the top bar</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Sender Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Name
                </label>
                <input
                  type="text"
                  value={formData.senderName}
                  onChange={(e) => handleInputChange('senderName', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Amazon, FedEx, etc."
                />
              </div>

              {/* Serial Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Serial/Tracking Number
                </label>
                <input
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) => handleInputChange('serialNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Tracking or reference number"
                />
              </div>
            </div>

            {/* Sender Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sender Address
              </label>
              <textarea
                value={formData.senderAddress}
                onChange={(e) => handleInputChange('senderAddress', e.target.value)}
                rows={2}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Sender's address"
              />
            </div>
          </div>
        </div>

        {/* Recipient Information */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Recipient Information</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            {/* User Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Recipient *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => handleUserSearchChange(e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Search by name or email..."
                />
                {searchingUsers && (
                  <div className="absolute right-3 top-2">
                    <LoadingSpinner inline size="xs" />
                  </div>
                )}
              </div>
              
              {/* User Dropdown */}
              {users.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg">
                  {users.map(user => (
                    <button
                      key={user.userId}
                      type="button"
                      onClick={() => {
                        handleInputChange('recipientUserId', user.userId)
                        setUserSearchTerm(`${user.firstName} ${user.lastName}`)
                        setUsers([])
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 border-b border-gray-200 last:border-b-0"
                    >
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </button>
                  ))}
                </div>
              )}
              {errors.recipientUserId && (
                <p className="mt-1 text-sm text-red-600">{errors.recipientUserId}</p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recipient Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Name
                </label>
                <input
                  type="text"
                  value={formData.recipientName}
                  onChange={(e) => handleInputChange('recipientName', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Auto-filled from selected user"
                />
              </div>

              {/* Building Unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building Unit
                </label>
                <select
                  value={formData.buildingUnitId}
                  onChange={(e) => handleInputChange('buildingUnitId', e.target.value)}
                  disabled={!formData.buildingId}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100"
                >
                  <option value="">Select a unit (optional)</option>
                  {buildingUnits.map(unit => (
                    <option key={unit.buildingUnitId} value={unit.buildingUnitId}>
                      {unit.unitNumber} - {unit.unitType}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Unit Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit Number
                </label>
                <input
                  type="text"
                  value={formData.recipientUnitNumber}
                  onChange={(e) => handleInputChange('recipientUnitNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="101, 2A, etc."
                />
              </div>

              {/* Floor Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Floor Number
                </label>
                <input
                  type="text"
                  value={formData.recipientFloorNumber}
                  onChange={(e) => handleInputChange('recipientFloorNumber', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="1, 2, Ground, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recipient Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={formData.recipientEmail}
                  onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Auto-filled from selected user"
                />
                {errors.recipientEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.recipientEmail}</p>
                )}
              </div>

              {/* Recipient Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recipient Phone
                </label>
                <input
                  type="tel"
                  value={formData.recipientPhone}
                  onChange={(e) => handleInputChange('recipientPhone', e.target.value)}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="Auto-filled from selected user"
                />
              </div>
            </div>

            {/* Recipient Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Address
              </label>
              <textarea
                value={formData.recipientAddress}
                onChange={(e) => handleInputChange('recipientAddress', e.target.value)}
                rows={2}
                className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="Recipient's full address"
              />
            </div>
          </div>
        </div>

        {/* Delivery Status */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Delivery Status</h3>
          </div>
          <div className="px-6 py-4 space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Is Delivered */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isDelivered}
                    onChange={(e) => handleInputChange('isDelivered', e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Mark as Delivered</span>
                </label>
                {formData.isDelivered && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Delivery Date *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.deliveryDate}
                      onChange={(e) => handleInputChange('deliveryDate', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {errors.deliveryDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.deliveryDate}</p>
                    )}
                  </div>
                )}
                {errors.isDelivered && (
                  <p className="mt-1 text-sm text-red-600">{errors.isDelivered}</p>
                )}
              </div>

              {/* Is Collected */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isCollected}
                    onChange={(e) => handleInputChange('isCollected', e.target.checked)}
                    disabled={!formData.isDelivered}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded disabled:opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">Mark as Collected</span>
                </label>
                {formData.isCollected && (
                  <div className="mt-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Collection Date *
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.collectedDate}
                      onChange={(e) => handleInputChange('collectedDate', e.target.value)}
                      className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                    {errors.collectedDate && (
                      <p className="mt-1 text-sm text-red-600">{errors.collectedDate}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/deliveries')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
            icon={<Save className="w-4 h-4" />}
          >
            {loading ? 'Creating...' : 'Create Delivery'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default CreateDelivery